import {
  BankReconciliationAccountingLine,
  BankReconciliationLineEdit,
} from 'lib/api/accounting/types';
import { showAlert } from 'lib/functions/utils';
import { useEffect, useMemo, useState } from 'react';
import { Dispatch } from 'redux';

import { ReconciliationFilterOption } from './components/search/filters';

type QueryFiltersState = {
  startDate: string;
  endDate: string;
  selectedBankId: number | null;
  reconciliationFilter: ReconciliationFilterOption;
  businessIdentifier: string;
  description: string;
  debit: string;
  credit: string;
  observation: string;
  conciliationDate: string | null;
  page: number;
  limit: number;
};

type UseBankReconciliationFiltersParams = {
  dispatch: Dispatch;
  defaultPeriod: { start: string; end: string };
  defaultLimit: number;
  normalizeDateOnly: (value: string) => string;
};

export const useBankReconciliationFilters = ({
  dispatch,
  defaultPeriod,
  defaultLimit,
  normalizeDateOnly,
}: UseBankReconciliationFiltersParams) => {
  const [searchStartDate, setSearchStartDate] = useState<string | null>(
    defaultPeriod.start,
  );
  const [searchEndDate, setSearchEndDate] = useState<string | null>(
    defaultPeriod.end,
  );
  const [searchSelectedBankId, setSearchSelectedBankId] = useState('');
  const [searchReconciliationFilter, setSearchReconciliationFilter] =
    useState<ReconciliationFilterOption>('all');
  const [searchBusinessIdentifier, setSearchBusinessIdentifier] = useState('');
  const [searchDescription, setSearchDescription] = useState('');
  const [searchDebit, setSearchDebit] = useState('');
  const [searchCredit, setSearchCredit] = useState('');
  const [searchObservation, setSearchObservation] = useState('');
  const [searchConciliationDate, setSearchConciliationDate] = useState<
    string | null
  >(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [queryFilters, setQueryFilters] = useState<QueryFiltersState>({
    startDate: defaultPeriod.start,
    endDate: defaultPeriod.end,
    selectedBankId: null,
    reconciliationFilter: 'all',
    businessIdentifier: '',
    description: '',
    debit: '',
    credit: '',
    observation: '',
    conciliationDate: null,
    page: 1,
    limit: defaultLimit,
  });

  const queryParams = useMemo(() => {
    const params = new URLSearchParams();
    params.append('startDate', normalizeDateOnly(queryFilters.startDate));
    params.append('endDate', normalizeDateOnly(queryFilters.endDate));
    params.append('page', String(queryFilters.page));
    params.append('limit', String(queryFilters.limit));
    params.append('pageSize', String(queryFilters.limit));
    if (queryFilters.selectedBankId !== null) {
      params.append('bankId', String(queryFilters.selectedBankId));
    }
    if (queryFilters.reconciliationFilter === 'reconciled') {
      params.append('isConciliated', 'true');
    }
    if (queryFilters.reconciliationFilter === 'unreconciled') {
      params.append('isConciliated', 'false');
    }
    if (queryFilters.businessIdentifier.trim().length > 0) {
      params.append(
        'businessIdentifier',
        queryFilters.businessIdentifier.trim(),
      );
    }
    if (queryFilters.description.trim().length > 0) {
      params.append('description', queryFilters.description.trim());
    }
    if (queryFilters.debit.trim().length > 0) {
      params.append('debit', queryFilters.debit.trim());
    }
    if (queryFilters.credit.trim().length > 0) {
      params.append('credit', queryFilters.credit.trim());
    }
    if (queryFilters.observation.trim().length > 0) {
      params.append('observation', queryFilters.observation.trim());
    }
    if (queryFilters.conciliationDate) {
      params.append(
        'conciliationDate',
        normalizeDateOnly(queryFilters.conciliationDate),
      );
    }
    return params.toString();
  }, [normalizeDateOnly, queryFilters]);

  const getMinimumFilters = () => {
    if (!searchStartDate || !searchEndDate) {
      showAlert(
        dispatch,
        'warning',
        'Debes seleccionar fecha de inicio y fecha de término.',
      );
      return;
    }
    if (!searchSelectedBankId) {
      showAlert(dispatch, 'warning', 'Debes seleccionar un banco.');
      return;
    }

    const parsedBankId = Number(searchSelectedBankId);
    if (Number.isNaN(parsedBankId)) {
      showAlert(dispatch, 'warning', 'Debes seleccionar un banco válido.');
      return null;
    }

    return {
      startDate: normalizeDateOnly(searchStartDate),
      endDate: normalizeDateOnly(searchEndDate),
      selectedBankId: parsedBankId,
    };
  };

  const applySearchFilters = () => {
    const minimumFilters = getMinimumFilters();
    if (!minimumFilters) return;

    setHasSearched(true);
    setQueryFilters((previous) => ({
      ...previous,
      startDate: minimumFilters.startDate,
      endDate: minimumFilters.endDate,
      selectedBankId: minimumFilters.selectedBankId,
      reconciliationFilter: searchReconciliationFilter,
      businessIdentifier: searchBusinessIdentifier,
      description: searchDescription,
      debit: searchDebit,
      credit: searchCredit,
      observation: searchObservation,
      conciliationDate: searchConciliationDate,
      page: 1,
    }));
  };

  return {
    hasSearched,
    queryFilters,
    queryParams,
    setQueryFilters,
    searchStartDate,
    setSearchStartDate,
    searchEndDate,
    setSearchEndDate,
    searchSelectedBankId,
    setSearchSelectedBankId,
    searchReconciliationFilter,
    setSearchReconciliationFilter,
    searchBusinessIdentifier,
    setSearchBusinessIdentifier,
    searchDescription,
    setSearchDescription,
    searchDebit,
    setSearchDebit,
    searchCredit,
    setSearchCredit,
    searchObservation,
    setSearchObservation,
    searchConciliationDate,
    setSearchConciliationDate,
    handleMinimumSearch: applySearchFilters,
    handleAdvancedSearch: applySearchFilters,
  };
};

type TableRowWithEditState = {
  line: BankReconciliationAccountingLine;
  rowKey: string;
  edit: BankReconciliationLineEdit;
  initialEdit: BankReconciliationLineEdit;
};

type UseBankReconciliationEditsParams = {
  items: BankReconciliationAccountingLine[];
  reconciliationFilter: ReconciliationFilterOption;
  toLineEdit: (
    line: BankReconciliationAccountingLine,
    index: number,
  ) => BankReconciliationLineEdit;
  areLineEditsEqual: (
    left?: BankReconciliationLineEdit,
    right?: BankReconciliationLineEdit,
  ) => boolean;
};

export const useBankReconciliationEdits = ({
  items,
  reconciliationFilter,
  toLineEdit,
  areLineEditsEqual,
}: UseBankReconciliationEditsParams) => {
  const [rowEdits, setRowEdits] = useState<
    Record<string, BankReconciliationLineEdit>
  >({});
  const [initialRowEdits, setInitialRowEdits] = useState<
    Record<string, BankReconciliationLineEdit>
  >({});

  useEffect(() => {
    const nextInitial: Record<string, BankReconciliationLineEdit> = {};
    items.forEach((line, index) => {
      const lineEdit = toLineEdit(line, index);
      nextInitial[lineEdit.rowKey] = lineEdit;
    });
    setInitialRowEdits(nextInitial);
    setRowEdits((previous) => {
      const nextEdits: Record<string, BankReconciliationLineEdit> = {};
      Object.keys(nextInitial).forEach((key) => {
        nextEdits[key] = previous[key] ?? nextInitial[key];
      });
      return nextEdits;
    });
  }, [items, toLineEdit]);

  const allTableRows = useMemo<TableRowWithEditState[]>(
    () =>
      items.map((line, index) => {
        const baseEdit = toLineEdit(line, index);
        const rowKey = baseEdit.rowKey;
        return {
          line,
          rowKey,
          edit: rowEdits[rowKey] ?? baseEdit,
          initialEdit: initialRowEdits[rowKey] ?? baseEdit,
        };
      }),
    [initialRowEdits, items, rowEdits, toLineEdit],
  );

  const dirtyRows = useMemo(
    () =>
      allTableRows.filter(
        (row) => !areLineEditsEqual(row.edit, row.initialEdit),
      ),
    [allTableRows, areLineEditsEqual],
  );

  const tableRows = useMemo(() => {
    if (reconciliationFilter === 'reconciled') {
      return allTableRows.filter((row) => row.edit.isReconciled);
    }
    if (reconciliationFilter === 'unreconciled') {
      return allTableRows.filter((row) => !row.edit.isReconciled);
    }
    return allTableRows;
  }, [allTableRows, reconciliationFilter]);

  const updateRowEdit = (
    rowKey: string,
    patch: Partial<Omit<BankReconciliationLineEdit, 'rowKey'>>,
  ) => {
    setRowEdits((previous) => ({
      ...previous,
      [rowKey]: {
        ...(previous[rowKey] ?? initialRowEdits[rowKey]),
        rowKey,
        ...patch,
      },
    }));
  };

  return {
    tableRows,
    allTableRows,
    dirtyRows,
    updateRowEdit,
    setInitialRowEdits,
  };
};
